import { useEffect, useState } from "react";
import { MapPin, X, CheckCircle2, AlertCircle } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useApp } from "@/store/AppContext";

export function LocationDialog({ open, onClose }) {
  const { setLocation, locationPin } = useApp();
  const [pin, setPin] = useState(locationPin || "");
  const [areas, setAreas] = useState([]);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) api.get("/service-areas").then(setAreas).catch(() => {}); }, [open]);
  if (!open) return null;
  const check = async (p) => {
    const code = (p || pin).trim();
    if (code.length !== 6) return setRes({ error: "Enter a 6-digit PIN code" });
    setBusy(true);
    try {
      const r = await api.post("/service-areas/check", { pincode: code });
      setRes(r);
      if (r.serviceable) { setLocation(`${r.area.city}, ${r.area.state}`, code); setTimeout(onClose, 700); }
    } catch (e) { setRes({ error: errMsg(e, "Could not check PIN code") }); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-[60] grid place-items-end sm:place-items-center" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl p-6 rise" data-testid="location-dialog">
        <button onClick={onClose} className="absolute right-4 top-4 p-1" aria-label="Close"><X size={20} /></button>
        <p className="eyebrow">Delivery location</p>
        <h2 className="font-display text-2xl font-semibold mt-1">Where should we deliver?</h2>
        <p className="text-sm text-[var(--on-surface3)] mt-1">Enter your PIN code to see serviceability and local prices.</p>
        <div className="flex gap-2 mt-4">
          <input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" placeholder="6-digit PIN code" className="input" data-testid="pincode-input" />
          <button onClick={() => check()} disabled={busy} className="btn-brand" data-testid="pincode-check-btn">Check</button>
        </div>
        {res?.error && <p className="mt-3 text-sm text-[var(--error)] flex items-center gap-1" data-testid="pincode-error"><AlertCircle size={16} /> {res.error}</p>}
        {res && !res.error && (res.serviceable
          ? <p className="mt-3 text-sm text-[var(--success)] flex items-center gap-1" data-testid="pincode-serviceable"><CheckCircle2 size={16} /> Great! We deliver to {res.area.city}{res.area.housing ? ` · ${res.area.housing}` : ""}.</p>
          : <p className="mt-3 text-sm text-[var(--warning)] flex items-center gap-1" data-testid="pincode-not-serviceable"><AlertCircle size={16} /> We don't deliver to this PIN code yet. Become an Area Partner to bring Jaivik Bazaar here!</p>)}
        {areas.length > 0 && <>
          <p className="label mt-5">Currently serving</p>
          <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
            {areas.filter((a) => a.active !== false).flatMap((a) => a.pincodes.map((p) => (
              <button key={p} onClick={() => { setPin(p); check(p); }} className="chip bg-[var(--surface3)] hover:bg-[var(--brand-secondary)]" data-testid={`area-pin-${p}`}><MapPin size={12} className="mr-1" />{a.city} {p}</button>
            )))}
          </div>
        </>}
      </div>
    </div>
  );
}
